<template>
  <article class="trial-exam">
    <h1>{{ name }} <TextToSpeech :text="name" /></h1>
    <p class="trial-exam-question-count">
      {{ questionCountText }}
      <TextToSpeech :text="questionCountText" />
    </p>
    <ErrorMessage v-if="errorMessage" class="title-screen">
      <p>{{ errorMessage }}</p>
      <p v-if="errorRetryCallback">
        <ThreeDButton icon="redo" @click="errorRetryCallback()">
          {{ $t('actions.retry') }}
        </ThreeDButton>
      </p>
    </ErrorMessage>
    <div v-else-if="state === TrialExamState.TITLE_SCREEN" class="title-screen">
      <div
        ref="description"
        class="trial-exam-description"
        v-html="formattedDescription"
      ></div>

      <p v-if="gradingAvailable">
        <ToggleButton
          v-model="acceptGrading"
          :labels="{
            on: $t('components.toggle-button.yes'),
            off: $t('components.toggle-button.no'),
          }"
        />
        {{ $t('components.trial-exam.accept-grading') }}
        <TextToSpeech :text="$t('components.trial-exam.accept-grading')" />
      </p>
      <p>
        <ToggleButton
          v-model="acceptTeacherAccess"
          :labels="{
            on: $t('components.toggle-button.yes'),
            off: $t('components.toggle-button.no'),
          }"
        />
        {{ $t('components.trial-exam.accept-teacher-access') }}
        <TextToSpeech
          :text="$t('components.trial-exam.accept-teacher-access')"
        />
      </p>

      <div class="start-button-wrapper">
        <ThreeDButton class="start-button" icon="play" @click="startTraining">
          {{ $t('components.trial-exam.start-button') }}
        </ThreeDButton>
      </div>
    </div>
    <template v-else-if="state === TrialExamState.TRAINING_IN_PROGRESS">
      <QuestionListManager
        v-if="testQuestions.length > 0"
        :user-answer-model="UserAnswerModel.TESTTRIALEXAM"
        :session-id="testSessionId"
        :instant-feedback="true"
        :is-homework="false"
        :finished-callback="finishedCallback"
      />
      <div v-else class="loading-animation-wrapper">
        <LoadingAnimation />
      </div>
    </template>
    <template v-else-if="state === TrialExamState.FINISHED">
      <QuestionListEvaluation
        ref="questionListEvaluation"
        :user-answer-model="UserAnswerModel.TESTTRIALEXAM"
        :session-id="testSessionId"
      />
      <div class="retry-button-wrapper">
        <ThreeDButton class="retry-button" icon="redo" @click="startTraining">
          {{ $t('components.trial-exam.retry-button') }}
        </ThreeDButton>
      </div>
    </template>
  </article>
</template>

<script>
import ErrorMessage from '@/components/ui/ErrorMessage'
import LoadingAnimation from '../LoadingAnimation'
import QuestionListEvaluation from '@/components/training/QuestionListEvaluation'
import QuestionListManager from '../QuestionListManager'
import TextToSpeech from '@/components/accessibility/TextToSpeech'
import ThreeDButton from '../ThreeDButton'
import ToggleButton from '@/components/ui/ToggleButton'

import textToSpeechMixin from '@/mixins/inject-text-to-speech'
import typesetMathMlMixin from '@/mixins/typeset-mathml'

import { AuthStoreNamespacedTypes } from '@/store/auth'
import { QuestionStoreNamespacedTypes } from '@/store/questions'
import { UserAnswerModel } from '@/enums'

/**
 * @enum {string}
 */
const TrialExamState = {
  TITLE_SCREEN: 'title-screen',
  TRAINING_IN_PROGRESS: 'training-in-progress',
  FINISHED: 'finished',
}

export default {
  name: 'TrialExam',
  mixins: [textToSpeechMixin, typesetMathMlMixin],
  components: {
    ErrorMessage,
    LoadingAnimation,
    QuestionListEvaluation,
    QuestionListManager,
    TextToSpeech,
    ThreeDButton,
    ToggleButton,
  },
  props: {
    name: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: '',
    },
    questionCount: {
      type: Number,
      default: 0,
    },
    testTemplateId: {
      type: Number,
      required: true,
    },
    classLevel: {
      type: Number,
      default: undefined,
    },
    gradingAvailable: {
      type: Boolean,
      default: false,
    },
    extraPostData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      TrialExamState,
      UserAnswerModel,
      /**
       * The current state of the component and its session.
       */
      state: TrialExamState.TITLE_SCREEN,

      /**
       * Data for the example question displayed before starting the session.
       */
      exampleQuestion: null,

      /**
       * The ID of the current test session.
       */
      testSessionId: null,

      /**
       * @type {boolean}
       */
      acceptGrading: false,

      /**
       * @type {boolean}
       */
      acceptTeacherAccess: true,

      /**
       * @type {string}
       */
      errorMessage: '',

      /**
       * @type {function}
       */
      errorRetryCallback: null,
    }
  },
  computed: {
    formattedDescription() {
      if (this.description.indexOf('<') !== 0) {
        return `<p>${this.description}</p>`
      }

      return this.description
    },
    questionCountText() {
      return this.$t('components.trial-exam.question-count', {
        count: this.questionCount,
      })
    },
    /**
     * The class level of the current user.
     * @returns {number}
     */
    userClass() {
      return this.$store.getters[
        AuthStoreNamespacedTypes.getters.GET_USER_CLASS
      ]()
    },

    /**
     * Question data for the session.
     */
    testQuestions() {
      return this.$store.getters[
        QuestionStoreNamespacedTypes.getters.GET_QUESTIONS
      ](UserAnswerModel.TESTTRIALEXAM, this.testSessionId)
    },
  },
  methods: {
    /**
     * Changes the view to the finished state.
     */
    finishedCallback() {
      this.state = TrialExamState.FINISHED

      this.$emit('finished')

      this.$store
        .dispatch(QuestionStoreNamespacedTypes.actions.FETCH_RESULT, {
          sessionId: this.testSessionId,
          userAnswerModel: UserAnswerModel.TESTTRIALEXAM,
        })
        .finally(() => {
          // Scroll to the evaluation component once rendered
          setTimeout(() => {
            this.$refs.questionListEvaluation.$el.scrollIntoView({
              block: 'start',
              behavior: 'smooth',
            })
          })
        })
    },

    /**
     * Calls the API to start a test session and applies the test data.
     */
    startTraining() {
      this.clearError()

      this.state = TrialExamState.TRAINING_IN_PROGRESS

      // Clear any existing test session ID, to make sure we're not displaying old data while loading the new data
      this.testSessionId = null

      this.$store
        .dispatch(QuestionStoreNamespacedTypes.actions.INITIALIZE_TRIAL_EXAM, {
          ...this.extraPostData,
          id: this.testTemplateId,
          grade: this.acceptGrading,
          history: this.acceptTeacherAccess,
          classLevel:
            this.classLevel !== undefined ? this.classLevel : this.userClass,
        })
        .then(sessionId => {
          this.testSessionId = sessionId
        })
        .catch(error => {
          this.setError(error.message, () => this.startTraining())
        })
    },

    /**
     * @param {string} message
     * @param {function=} retryCallback
     */
    setError(message, retryCallback) {
      this.errorMessage = message
      this.errorRetryCallback = retryCallback || null
    },

    clearError() {
      this.errorMessage = ''
      this.errorRetryCallback = null
    },

    updateTextToSpeech() {
      if (this.$refs.description) {
        this.injectTextToSpeech(this.$refs.description)
      }
    },
  },
  watch: {
    /**
     * Clears the test data whenever the test template ID changes.
     */
    testTemplateId: {
      handler() {
        this.state = TrialExamState.TITLE_SCREEN

        this.testSessionId = null
      },
      immediate: true,
    },
  },
  mounted() {
    this.updateTextToSpeech()
  },
  updated() {
    this.updateTextToSpeech()
  },
}
</script>

<style lang="scss" scoped>
.trial-exam {
  position: relative;
}

.trial-exam-question-count {
  font-size: 1.5em;
  color: $blueVeryDark;
}

.title-screen,
.question-list-evaluation {
  margin: var(--content-holder-margin) 0;
  padding: var(--content-padding-large);
  background: $white;
  border-radius: var(--border-radius);
  overflow: hidden;

  @media (max-width: $minWidthSmallTablet) {
    border-radius: 0;
  }
}

.title-screen {
  font-size: $copyFontSize;
}

.loading-animation-wrapper {
  padding: 2em;
  text-align: center;
  font-size: 3em;
}

.start-button-wrapper {
  margin: 2em 1em 1em;
  text-align: center;
}

.retry-button-wrapper {
  font-size: 1.5em;
  text-align: center;
  padding: var(--content-padding-large);
}
</style>
